import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Users,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Award,
  Copy,
  Check,
  Share2,
  CheckCircle2,
  Sparkles,
  Info
} from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { TIERS, getTierColorClass, formatReferralRange, getTierById } from "@/config/tiers";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Tier Progress & Benefits Component
const TierProgressSection = ({ currentTier, activeReferrals, tierProgress }) => {
  const currentTierData = getTierById(currentTier || "bronze");
  const currentTierIndex = TIERS.findIndex((t) => t.id === currentTierData.id);
  const nextTier = currentTierIndex < TIERS.length - 1 ? TIERS[currentTierIndex + 1] : null;

  // Calculate progress percentage (use tierProgress if available, otherwise compute)
  const progressPercent = tierProgress?.progress_percent ?? (() => {
    if (!nextTier) return 100;
    const range = nextTier.minReferrals - currentTierData.minReferrals;
    const progress = activeReferrals - currentTierData.minReferrals;
    return Math.min(100, Math.max(0, (progress / range) * 100));
  })();

  const referralsNeeded = nextTier 
    ? Math.max(0, nextTier.minReferrals - activeReferrals)
    : 0;

  return (
    <Card className="bg-white border-slate-100 shadow-sm" data-testid="tier-progress-section">
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-purple-600" />
          Tier Progress & Benefits
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Progress Bar to Next Tier */}
        {nextTier && (
          <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Badge className={getTierColorClass(currentTierData.id)}>
                  {currentTierData.name}
                </Badge>
                <span className="text-slate-400">→</span>
                <Badge className={getTierColorClass(nextTier.id)}>
                  {nextTier.name}
                </Badge>
              </div>
              <span className="text-sm font-medium text-slate-600">
                {referralsNeeded} referrals to go
              </span>
            </div>
            <Progress value={progressPercent} className="h-3" />
            <p className="text-xs text-slate-500 mt-2">
              {activeReferrals} active referrals • Next tier at {nextTier.minReferrals} referrals
            </p>
          </div>
        )}

        {/* All Tiers Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {TIERS.map((tier) => {
            const isCurrentTier = tier.id === currentTierData.id;
            const isPastTier = TIERS.indexOf(tier) < currentTierIndex;
            const isFutureTier = TIERS.indexOf(tier) > currentTierIndex;

            return (
              <div
                key={tier.id}
                className={`relative p-4 rounded-xl border-2 transition-all ${
                  isCurrentTier
                    ? "border-purple-500 bg-purple-50/50 shadow-md"
                    : isPastTier
                    ? "border-slate-200 bg-slate-50 opacity-60"
                    : "border-slate-100 bg-white"
                }`}
                data-testid={`tier-card-${tier.id}`}
              >
                {/* Current Tier Badge */}
                {isCurrentTier && (
                  <div className="absolute -top-2 -right-2">
                    <Badge className="bg-purple-600 text-white text-xs px-2 py-0.5 shadow-sm">
                      Current
                    </Badge>
                  </div>
                )}

                {/* Tier Header */}
                <div className="flex items-center gap-2 mb-3">
                  <Badge className={`${getTierColorClass(tier.id)} font-medium`}>
                    {tier.name}
                  </Badge>
                </div>

                {/* Commission Rate */}
                <p className="text-2xl font-bold text-slate-900 mb-1">
                  {tier.commissionRate}%
                </p>
                <p className="text-xs text-slate-500 mb-3">
                  {formatReferralRange(tier)} referrals
                </p>

                {/* Benefits List */}
                <ul className="space-y-1.5">
                  {tier.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start gap-1.5 text-xs text-slate-600">
                      <CheckCircle2 className={`w-3.5 h-3.5 mt-0.5 flex-shrink-0 ${
                        isCurrentTier || isPastTier ? "text-emerald-500" : "text-slate-300"
                      }`} />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Retroactive Earnings Explanation */}
        <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
          <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-900 mb-1">
              Retroactive Commission Increase
            </p>
            <p className="text-xs text-blue-700 leading-relaxed">
              When you advance to the next tier, all your existing referrals automatically earn at the new higher commission rate. 
              Your past earnings are recalculated and the difference is added to your next payout.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [referrals, setReferrals] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    document.title = "Dashboard | GeniusList Partners";
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [overviewRes, referralsRes, chartRes] = await Promise.all([
        axios.get(`${API}/dashboard/overview`, { withCredentials: true }),
        axios.get(`${API}/dashboard/recent-referrals?limit=10`, { withCredentials: true }),
        axios.get(`${API}/dashboard/activity-chart?days=30`, { withCredentials: true })
      ]);
      
      setOverview(overviewRes.data);
      setReferrals(referralsRes.data);
      setChartData(chartRes.data);
    } catch (error) {
      console.error("Dashboard fetch error:", error);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const copyReferralLink = async () => {
    if (overview?.referral_link) {
      await navigator.clipboard.writeText(overview.referral_link);
      setCopied(true);
      toast.success("Referral link copied!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareToSocial = (platform) => {
    const link = encodeURIComponent(overview?.referral_link || "");
    const text = encodeURIComponent("Join GeniusList with my referral link and get started on your journey! 🚀");
    const shortText = encodeURIComponent("Join GeniusList with my referral link!");
    
    const urls = {
      instagram: `https://www.instagram.com/`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${link}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${link}&quote=${shortText}`,
      twitter: `https://twitter.com/intent/tweet?url=${link}&text=${text}`,
      whatsapp: `https://wa.me/?text=${text}%20${link}`,
      email: `mailto:?subject=${encodeURIComponent("Check out GeniusList!")}&body=${text}%20${link}`,
    };
    
    if (platform === 'email') {
      window.location.href = urls[platform];
    } else {
      window.open(urls[platform], "_blank");
    }
    
    toast.success(`Opening ${platform.charAt(0).toUpperCase() + platform.slice(1)}...`);
  };

  const copyWithMessage = async () => {
    const message = `Hey! I've been using GeniusList and thought you might like it too. Use my referral link to sign up: ${overview?.referral_link}`;
    try {
      await navigator.clipboard.writeText(message);
      toast.success("Message with link copied!");
    } catch {
      toast.error("Failed to copy");
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      active: "badge-active",
      pending: "badge-pending",
      churned: "badge-churned"
    };
    return badges[status] || "badge-pending";
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="bg-white border-slate-100">
              <CardContent className="p-6">
                <Skeleton className="h-4 w-24 mb-4" />
                <Skeleton className="h-8 w-32 mb-2" />
                <Skeleton className="h-4 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  const metrics = [
    {
      title: "Total Referrals",
      value: overview?.total_referrals || 0,
      change: overview?.referral_change_percent || 0,
      icon: Users,
      color: "text-purple-600 bg-purple-50"
    },
    {
      title: "Active Subscriptions",
      value: overview?.active_subscriptions || 0,
      subtitle: `${overview?.retention_rate || 0}% retention`,
      icon: TrendingUp,
      color: "text-emerald-600 bg-emerald-50"
    },
    {
      title: "Total Earnings",
      value: `$${(overview?.total_earnings || 0).toLocaleString()}`,
      subtitle: `$${overview?.earnings_this_month || 0} this month`,
      icon: DollarSign,
      color: "text-blue-600 bg-blue-50"
    },
    {
      title: "Current Tier",
      value: overview?.current_tier?.charAt(0).toUpperCase() + overview?.current_tier?.slice(1) || "Bronze",
      subtitle: `${overview?.commission_rate || 30}% commission`,
      icon: Award,
      color: "text-amber-600 bg-amber-50",
      isTier: true
    }
  ];

  return (
    <div className="space-y-6">
      {/* Performance Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {metrics.map((metric, idx) => {
          const Icon = metric.icon;
          return (
            <Card key={idx} className="bg-white border-slate-100 shadow-sm card-hover" data-testid={`metric-card-${idx}`}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-500">{metric.title}</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">
                      {metric.isTier ? (
                        <span className={`px-3 py-1 rounded-full text-sm ${getTierColorClass(overview?.current_tier)}`}>
                          {metric.value}
                        </span>
                      ) : (
                        metric.value
                      )}
                    </p>
                    {metric.change !== undefined && (
                      <div className={`flex items-center gap-1 mt-2 text-sm ${metric.change >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
                        {metric.change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                        {Math.abs(metric.change)}% this month
                      </div>
                    )}
                    {metric.subtitle && (
                      <p className="text-sm text-slate-500 mt-2">{metric.subtitle}</p>
                    )}
                  </div>
                  <div className={`p-3 rounded-xl ${metric.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
                
                {metric.isTier && overview?.tier_progress && (
                  <div className="mt-4">
                    <div className="flex justify-between text-xs text-slate-500 mb-1">
                      <span>Progress to {overview.tier_progress.next_tier || "Max"}</span>
                      <span>{overview.tier_progress.progress_percent}%</span>
                    </div>
                    <Progress value={overview.tier_progress.progress_percent} className="h-2" />
                    {overview.tier_progress.next_tier && (
                      <p className="text-xs text-slate-500 mt-1">
                        {overview.tier_progress.referrals_needed} more referrals needed
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Tier Progress & Benefits */}
      <TierProgressSection 
        currentTier={overview?.current_tier} 
        activeReferrals={overview?.active_subscriptions || 0}
        tierProgress={overview?.tier_progress}
      />

      {/* Main Content Grid */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Recent Referrals & Chart */}
        <div className="lg:col-span-2 space-y-6">
          {/* Activity Chart */}
          <Card className="bg-white border-slate-100 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">
                Referral Activity (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData.slice(-14)}>
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(val) => formatDate(val)}
                      tick={{ fontSize: 12 }}
                      stroke="#94a3b8"
                    />
                    <YAxis 
                      tick={{ fontSize: 12 }}
                      stroke="#94a3b8"
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#fff', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px'
                      }}
                      labelFormatter={(val) => formatDate(val)}
                    />
                    <Bar 
                      dataKey="referrals" 
                      fill="url(#gradient)" 
                      radius={[4, 4, 0, 0]}
                    />
                    <defs>
                      <linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#6B46C1" />
                        <stop offset="100%" stopColor="#3B82F6" />
                      </linearGradient>
                    </defs>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Recent Referrals Table */}
          <Card className="bg-white border-slate-100 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">
                Recent Referrals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {referrals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center text-slate-500 py-8">
                        No referrals yet. Share your link to get started!
                      </TableCell>
                    </TableRow>
                  ) : (
                    referrals.map((referral, idx) => (
                      <TableRow key={idx} data-testid={`referral-row-${idx}`}>
                        <TableCell className="font-medium text-slate-900">
                          {referral.referred_name}
                        </TableCell>
                        <TableCell className="text-slate-500">
                          {formatDate(referral.signup_date)}
                        </TableCell>
                        <TableCell>
                          <Badge className={`${getStatusBadge(referral.status)} capitalize`}>
                            {referral.status}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium text-emerald-600">
                          ${referral.commission_earned?.toFixed(2) || "0.00"}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Referral Link & Sharing */}
        <div className="space-y-6">
          {/* Referral Link Card */}
          <Card className="bg-white border-slate-100 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900">
                My Referral Link
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-2">
                <Input 
                  value={overview?.referral_link || ""}
                  readOnly
                  className="text-sm bg-slate-50"
                  data-testid="referral-link-input"
                />
                <Button 
                  onClick={copyReferralLink}
                  variant="outline"
                  size="icon"
                  data-testid="copy-link-btn"
                >
                  {copied ? <Check className="w-4 h-4 text-emerald-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
              
              <div className="text-center py-4">
                <p className="text-sm text-slate-500 mb-2">Partner Code</p>
                <p className="text-2xl font-bold gradient-text">{overview?.partner_code}</p>
              </div>

              {/* QR Code */}
              <div className="bg-slate-50 rounded-xl p-4 flex flex-col items-center">
                <div className="bg-white rounded-lg p-2 border border-slate-200">
                  <QRCodeSVG 
                    value={overview?.referral_link || "https://geniuslist.io"} 
                    size={120}
                    level="M"
                    fgColor="#6B46C1"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">Scan to share</p>
              </div>
            </CardContent>
          </Card>

          {/* Share Buttons */}
          <Card className="bg-white border-slate-100 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Share2 className="w-5 h-5" />
                Share Your Link
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Copy with Message Button */}
              <Button 
                onClick={copyWithMessage}
                className="w-full gradient-btn text-white justify-start gap-3"
                data-testid="copy-message-btn"
              >
                <Copy className="w-5 h-5" />
                Copy Link with Message
              </Button>

              <div className="grid grid-cols-2 gap-2">
                {/* Twitter/X */}
                <Button 
                  onClick={() => shareToSocial('twitter')}
                  variant="outline"
                  className="justify-start gap-2"
                  data-testid="share-twitter-btn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                  </svg>
                  X
                </Button>
                
                {/* WhatsApp */}
                <Button 
                  onClick={() => shareToSocial('whatsapp')}
                  variant="outline"
                  className="justify-start gap-2"
                  data-testid="share-whatsapp-btn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                  </svg>
                  WhatsApp
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {/* LinkedIn */}
                <Button 
                  onClick={() => shareToSocial('linkedin')}
                  variant="outline"
                  className="justify-start gap-2"
                  data-testid="share-linkedin-btn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                  </svg>
                  LinkedIn
                </Button>
                
                {/* Facebook */}
                <Button 
                  onClick={() => shareToSocial('facebook')}
                  variant="outline"
                  className="justify-start gap-2"
                  data-testid="share-facebook-btn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                  Facebook
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {/* Instagram */}
                <Button 
                  onClick={() => shareToSocial('instagram')}
                  variant="outline"
                  className="justify-start gap-2"
                  data-testid="share-instagram-btn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  Instagram
                </Button>
                
                {/* Email */}
                <Button 
                  onClick={() => shareToSocial('email')}
                  variant="outline"
                  className="justify-start gap-2"
                  data-testid="share-email-btn"
                >
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                    <polyline points="22,6 12,13 2,6"/>
                  </svg>
                  Email
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
